import { FC, useEffect, useState } from 'react'

import { useParams, useSearch } from '@tanstack/react-router'

import { Alert, Card, CardContent, Cluster, Grid, Separator, Skeleton, Stack } from '@forto-dev/phoenix-ui-core'
import { Layout } from '@forto-dev/phoenix-ui-core/ship'

import { Back } from '@/fragments/back'
import { Footer } from '@/fragments/footer'
import { Header } from '@/fragments/header'
import { useLocalisation } from '@/utils/localisation'

const newlyCreatedAlertDelayMs = 2_000

// In some cases propagating a shipment from v1 to v2 can take a few minutes.
// We avoid showing the shipment not found error and let the user know what's happening.
const NewlyCreatedAlert: FC = () => {
  const { isNewlyCreated } = useSearch({ strict: false })
  const { id } = useParams({ strict: false })
  const { translate } = useLocalisation()
  const [showAlert, setShowAlert] = useState(false)

  useEffect(() => {
    if (!isNewlyCreated) return
    const timer = setTimeout(() => setShowAlert(true), newlyCreatedAlertDelayMs)
    return () => clearTimeout(timer)
  }, [isNewlyCreated])

  if (!showAlert) {
    return null
  }

  return (
    <Alert
      variant="muted"
      title={translate('shipment.newly_created_loading.title', { id })}
      description={translate('shipment.newly_created_loading.description')}
    />
  )
}

export const ShipmentHeaderSkeleton: FC = () => {
  return (
    <Cluster gap={8} x="between" y="center">
      <Cluster gap={16} y="center" breakpoint="sm">
        <Cluster gap={8} y="center">
          <Skeleton variant="icon" />
          <Stack gap={4}>
            <Skeleton variant="badge" />
            <Skeleton variant="content" size={2} />
          </Stack>
        </Cluster>
        <Separator orientation="vertical" />
        <Skeleton variant="button" />
        <Skeleton variant="button" />
        <Skeleton variant="button" />
        <Skeleton variant="button" />
        <Skeleton variant="icon" />
      </Cluster>
      <Cluster gap={8} y="center">
        <Skeleton variant="avatar" />
        <Skeleton variant="avatar" />
        <Skeleton variant="avatar" />
        <Skeleton variant="avatar" />
        <Skeleton variant="icon" />
      </Cluster>
    </Cluster>
  )
}

const ShipmentSidebarSkeleton: FC = () => {
  return (
    <Stack gap={16}>
      <Skeleton variant="content" size={24} fullWidth />
      <Card>
        <CardContent>
          <Stack gap={4}>
            <Skeleton variant="badge" />
            <Skeleton variant="content" size={2} />
          </Stack>
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <Stack gap={8}>
            <Skeleton variant="badge" />
            <Skeleton variant="content" fullWidth />
            <Skeleton variant="content" size={16} fullWidth />
            <Skeleton variant="content" size={16} fullWidth />
            <Skeleton variant="badge" />
            <Skeleton variant="content" fullWidth />
            <Skeleton variant="content" size={16} fullWidth />
            <Skeleton variant="content" size={16} fullWidth />
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  )
}

const ShipmentMainSkeleton: FC = () => {
  return (
    <Stack gap={24}>
      <Card>
        <CardContent>
          <Stack gap={16}>
            <Skeleton variant="badge" />
            <Skeleton variant="content" size={16} fullWidth />
            <Cluster gap={8}>
              <Skeleton variant="badge" />
              <Skeleton variant="badge" />
              <Skeleton variant="badge" />
              <Skeleton variant="badge" />
              <Skeleton variant="badge" />
            </Cluster>
          </Stack>
        </CardContent>
      </Card>
      <Cluster gap={16} fullWidth>
        <Skeleton variant="icon" />
        <Stack gap={8} fullWidth>
          <Grid col={2}>
            <Skeleton variant="badge" fullWidth />
          </Grid>
          <Grid col={4}>
            <Skeleton variant="content" fullWidth />
          </Grid>
          <Stack />
          <Card>
            <CardContent>
              <Stack gap={24}>
                <Stack gap={8}>
                  <Skeleton variant="content" fullWidth />
                  <Grid col={2}>
                    <Skeleton variant="content" fullWidth />
                  </Grid>
                </Stack>
                <Stack gap={12}>
                  <Grid col={2}>
                    <Skeleton variant="badge" fullWidth />
                  </Grid>
                  <Skeleton variant="content" fullWidth size={16} />
                  <Skeleton variant="button" />
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </Cluster>
      <Cluster gap={16} fullWidth>
        <Skeleton variant="icon" />
        <Stack gap={8} fullWidth>
          <Grid col={2}>
            <Skeleton variant="badge" fullWidth />
          </Grid>
          <Grid col={4}>
            <Skeleton variant="content" fullWidth />
          </Grid>
          <Stack />
          <Card>
            <CardContent>
              <Stack gap={24}>
                <Cluster gap={8} fullWidth>
                  <Skeleton variant="avatar" />
                  <Stack gap={8} fullWidth>
                    <Skeleton variant="content" fullWidth />
                    <Skeleton variant="content" fullWidth />
                    <Grid col={2}>
                      <Skeleton variant="content" fullWidth />
                    </Grid>
                  </Stack>
                </Cluster>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </Cluster>
    </Stack>
  )
}

const ShipmentContextSkeleton: FC = () => {
  return (
    <Stack gap={16}>
      <Stack gap={8}>
        <Card>
          <CardContent>
            <Stack gap={8}>
              <Skeleton variant="badge" />

              <Skeleton variant="button" fullWidth />
              <Skeleton variant="button" fullWidth />
              <Skeleton variant="content" />
            </Stack>
          </CardContent>
        </Card>
      </Stack>
      <Stack gap={12}>
        {Array.from({ length: 4 }).map((_) => (
          <>
            <Cluster gap={8} fullWidth>
              <Skeleton variant="icon" />
              <Skeleton variant="button" fullWidth />
            </Cluster>
            <Separator orientation="horizontal" />
          </>
        ))}
      </Stack>

      <Skeleton variant="content" size={16} fullWidth />
    </Stack>
  )
}

export const ShipmentSkeleton: FC = () => {
  return (
    <Layout
      header={<Header />}
      navigation={<Back to="/" />}
      title={<ShipmentHeaderSkeleton />}
      hideTitleBackgroundImage
      main={
        <Stack gap={16}>
          <NewlyCreatedAlert />
          <ShipmentMainSkeleton />
        </Stack>
      }
      footer={<Footer />}
      sidebar={<ShipmentSidebarSkeleton />}
      context={<ShipmentContextSkeleton />}
    />
  )
}
